import { tokenService } from '@features/authentication/services/tokenService';
import { ensureFreshAccessTokenForStream } from '@shared/api/ensureFreshAccessTokenForStream';
import type { DocStreamEvent } from '../types/documentCapture.types';

/**
 * EventSource wrapper for the Document Capture journey stream (86d3rrfbf) — same shape as the
 * quotation/sales-order/gate streams. Each message is ONE phase change of ONE file, not a
 * snapshot; the consumer morphs the matching row in place.
 *
 * EventSource can't set headers, so the JWT rides as `access_token`; the API accepts a query
 * token only on the allow-listed stream paths. Auto-reconnects with exponential backoff — but a
 * BOUNDED number of times; see MAX_RECONNECT_ATTEMPTS.
 *
 * Server-side the stream is already filtered to what the caller may see — a family you can't
 * view never reaches this client, so there is no client-side filtering to get wrong.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;
const STREAM_PATH = '/document-capture/stream';
const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

/**
 * 86d3y0cku — the reconnect budget, and the reason this file changed at all.
 *
 * `/document-capture/stream` now sits behind the DSCN module gate, so while Document Capture is
 * disabled the API answers 503. EventSource cannot read a response body or a status code — it only
 * fires `onerror` — so this client cannot tell "module switched off" from "API restarting" and used
 * to retry on the 30s ceiling FOREVER. nginx has a dedicated `location /api/document-capture/stream`
 * block, so every one of those retries reaches the API: each browser tab left open on the page
 * became a permanent load source for the entire disabled window, multiplied by however many tabs
 * the warehouse leaves open overnight.
 *
 * Ten attempts is ~181s of trying (1+2+4+8+16+30×5), chosen to comfortably outlast an API deploy or
 * restart — the common, genuinely-transient cause — so a bounded budget is not a resilience
 * regression in the case that actually happens. When it runs out we stop, report 'offline', and let
 * `onGaveUp` ask the ONE question EventSource cannot: is the module still available? That single
 * request settles it — if DSCN is off, ModuleGuard swaps the page for its notice and this handle is
 * closed by the bus; if it is a real outage the availability call fails too and the user keeps the
 * page, the honest 'offline' state, and a manual retry.
 */
const MAX_RECONNECT_ATTEMPTS = 10;

/** 'offline' is TERMINAL: the budget is spent and nothing is scheduled. Only retryNow() leaves it. */
export type DocStreamConnectionState = 'connecting' | 'open' | 'reconnecting' | 'offline';

export interface DocStreamHandlers {
  onEvent: (event: DocStreamEvent) => void;
  onConnectionStateChange?: (state: DocStreamConnectionState) => void;
  /**
   * Fired once when the reconnect budget is exhausted. The stream deliberately does not know what a
   * "module" is — the bus owns that question — so this is a callback rather than a store read here.
   */
  onGaveUp?: () => void;
}

export interface DocStreamHandle {
  close: () => void;
  /** Reset the budget and reconnect immediately. Safe to call in any state; a no-op once closed. */
  retryNow: () => void;
}

export function openDocumentCaptureStream(handlers: DocStreamHandlers): DocStreamHandle {
  let source: EventSource | null = null;
  let backoff = MIN_BACKOFF_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  let hasConnectedOnce = false;
  /** Consecutive failures since the last successful open. Reset by onopen and by retryNow(). */
  let attempts = 0;

  const emitState = (next: DocStreamConnectionState) => handlers.onConnectionStateChange?.(next);

  function buildUrl(): string {
    const accessToken = tokenService.getAccessToken();
    const params = new URLSearchParams();
    if (accessToken) params.set('access_token', accessToken);
    return `${API_BASE_URL}${STREAM_PATH}?${params.toString()}`;
  }

  function connect() {
    if (closed) return;
    emitState(hasConnectedOnce ? 'reconnecting' : 'connecting');
    try {
      source = new EventSource(buildUrl());
    } catch {
      scheduleReconnect();
      return;
    }

    source.onopen = () => {
      backoff = MIN_BACKOFF_MS;
      attempts = 0;
      hasConnectedOnce = true;
      emitState('open');
    };
    source.onmessage = (msg) => {
      if (!msg.data) return;
      try {
        handlers.onEvent(JSON.parse(msg.data) as DocStreamEvent);
      } catch (err) {
        console.warn('Failed to parse document-capture stream event:', err, msg.data);
      }
    };
    source.onerror = () => {
      source?.close();
      source = null;
      scheduleReconnect();
    };
  }

  function scheduleReconnect() {
    if (closed) return;

    // Budget spent — stop scheduling and hand the question over. Note we count ATTEMPTS, not elapsed
    // time: a connection that opens and immediately dies still resets the counter via onopen, so a
    // flapping-but-alive server is treated as healthy rather than being cut off.
    attempts += 1;
    if (attempts > MAX_RECONNECT_ATTEMPTS) {
      emitState('offline');
      handlers.onGaveUp?.();
      return;
    }

    emitState('reconnecting');
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null;
      // E7 item 4 — refresh before reconnecting; see quotationEventStream for the full rationale.
      await ensureFreshAccessTokenForStream();
      if (closed) return;
      connect();
    }, backoff);
    backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
  }

  connect();

  return {
    close() {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (source) {
        source.close();
        source = null;
      }
    },

    retryNow() {
      if (closed) return;
      // A fresh budget, and drop whatever is pending: this is called from a deliberate signal (the
      // user asking, or the tab coming back to the foreground), so waiting out a 30s backoff first
      // would make the app feel broken for half a minute after the user already asked it to fix itself.
      attempts = 0;
      backoff = MIN_BACKOFF_MS;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (source) {
        source.close();
        source = null;
      }
      // E7 item 4 — this path needs the refresh MORE than the backoff path, not less: it is reached
      // exactly when a tab wakes after a long sleep or a user asks a dead stream to recover, which is
      // precisely when the stored token is stale. Kept synchronous-looking to callers (the handle's
      // signature is unchanged); `closed` is re-checked because the await yields.
      void ensureFreshAccessTokenForStream().then(() => {
        if (closed) return;
        connect();
      });
    },
  };
}
